import React, { useEffect, useState } from 'react';
import { codingService, CodingProblem } from '../api/services/coding';
import CodingProblemList from '../components/Coding/CodingProblemList';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Container from '../components/common/Container';

const Coding: React.FC = () => {
  const [problems, setProblems] = useState<CodingProblem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<string[]>([]);
  const [companies, setCompanies] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [difficulty, setDifficulty] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const getCategoryLabel = (category: string) => {
    return category.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  useEffect(() => {
    const fetchProblems = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params: any = {
          page,
          per_page: 9,
          sort_by: 'difficulty',  // Add sorting parameter
          sort_order: 'asc'       // Ascending order (very_easy to very_difficult)
        };
        if (selectedCategory) params.category = selectedCategory;
        if (selectedCompany) params.company = selectedCompany;
        if (difficulty) params.difficulty = difficulty;
        if (search) params.search = search;
        const res = await codingService.listProblems(params);
        setProblems(res.data.items);
        setTotal(res.data.total);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Failed to load problems');
      } finally {
        setIsLoading(false);
      }
    };
    fetchProblems();
  }, [page, selectedCategory, selectedCompany, difficulty, search]);

  useEffect(() => {
    codingService.getCategories().then(res => setCategories(res.data));
    codingService.getCompanies().then(res => setCompanies(res.data));
  }, []);

  return (
    <Container className="px-4 py-6 min-h-screen flex flex-col">
      <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full">
        <h1 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Coding Problems</h1>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <select
            value={selectedCategory || ''}
            onChange={e => setSelectedCategory(e.target.value || null)}
            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">All Categories</option>
            {categories.map(cat => <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>)}
          </select>

          <select
            value={selectedCompany || ''}
            onChange={e => setSelectedCompany(e.target.value || null)}
            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">All Companies</option>
            {companies.map(comp => <option key={comp} value={comp}>{comp}</option>)}
          </select>

          <select
            value={difficulty || ''}
            onChange={e => setDifficulty(e.target.value || null)}
            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">All Difficulties</option>
            <option value="very_easy">Very Easy</option>
            <option value="easy">Easy</option>
            <option value="moderate">Moderate</option>
            <option value="difficult">Difficult</option>
            <option value="very_difficult">Very Difficult</option>
          </select>

          <input
            type="text"
            placeholder="Search problems..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white flex-1 min-w-[200px]"
          />
        </div>

        {/* Problems List */}
        <CodingProblemList
          problems={problems}
          isLoading={isLoading}
          error={error}
        />

        {/* Pagination */}
        {total > 9 && (
          <div className="flex justify-center mt-8">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Previous
                </button>

                <span className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  Page {page} of {Math.ceil(total / 10)}
                </span>

                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= Math.ceil(total / 10)}
                  className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Container>
  );
};

export default Coding;